import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { MapPin } from "lucide-react";
import { RequestButton, WhatsAppButton } from "./Buttons";

const LINES = ["Power Tool", "Repair &", "Refurbishment"];
const ease = [0.22, 1, 0.36, 1];

const MaskedLine = ({ text, i }) => (
  <span className="block overflow-hidden pb-[0.04em]">
    <motion.span className={`block ${i === 1 ? "text-[var(--red)]" : ""}`} initial={{ y: "105%" }} animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.15 + i * 0.13, ease }}>
      {text}
    </motion.span>
  </span>
);

const TiltMark = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-16, 16]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div className="relative aspect-square w-full max-w-[560px] [perspective:1200px]" onPointerMove={onMove} onPointerLeave={reset} data-testid="hero-logo-tilt">
      <div className="absolute inset-[8%] rounded-full bg-[radial-gradient(circle,rgba(227,0,27,0.28),transparent_65%)] blur-2xl" aria-hidden />
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative h-full w-full chamfer metal metal-edge border border-[var(--line)] flex items-center justify-center"
        initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.5, ease }}>
        <div className="absolute inset-0 grid-lines opacity-60" aria-hidden />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(255,255,255,0.12),transparent_55%)]" aria-hidden />
        <img src="/brand/ne-logo-mark.png" alt="NE Tool Repair logo" width="1075" height="570" className="relative w-[72%] mix-blend-lighten drop-shadow-[0_30px_40px_rgba(0,0,0,0.6)]" style={{ transform: "translateZ(60px)" }} />
        {["top-4 left-4", "top-4 right-4", "bottom-4 left-4", "bottom-4 right-4"].map((p) => (
          <span key={p} className={`absolute ${p} h-2.5 w-2.5 rounded-full bg-[#3a3f47] shadow-[inset_0_1px_1px_rgba(255,255,255,0.3)]`} aria-hidden />
        ))}
        <span className="absolute bottom-5 left-1/2 -translate-x-1/2 font-mono text-[11px] tracking-[0.3em] text-[var(--mute)] uppercase" style={{ transform: "translateZ(30px)" }}>Newcastle upon Tyne</span>
      </motion.div>
    </div>
  );
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMark = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative overflow-hidden border-b border-[var(--line)]" data-testid="hero-section">
      <div className="absolute inset-0 grid-lines [mask-image:linear-gradient(to_bottom,black,transparent)]" aria-hidden />
      <div className="absolute -right-40 -top-40 h-[600px] w-[600px] rounded-full bg-[radial-gradient(circle,rgba(227,0,27,0.12),transparent_60%)]" aria-hidden />
      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 pt-12 md:pt-20 pb-16 md:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease }}
            className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.12em] sm:tracking-[0.22em] text-[var(--mute)]">
            <MapPin className="h-4 w-4 text-[var(--red)]" aria-hidden /> Independent workshop · North East England
          </motion.p>
          <h1 className="mt-6 font-display font-black uppercase leading-[0.86] tracking-[-0.01em] text-[15vw] sm:text-7xl lg:text-[6.2rem] xl:text-[7.2rem]" data-testid="hero-headline">
            {LINES.map((l, i) => <MaskedLine key={l} text={l} i={i} />)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7, ease }}
            className="mt-8 max-w-xl text-base md:text-lg text-[var(--grey)] leading-relaxed" data-testid="hero-subheading">
            Milwaukee M12 &amp; M18 tool repair in Newcastle and the North East.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85, ease }}
            className="mt-10 flex flex-col sm:flex-row gap-3">
            <RequestButton testId="hero-request-repair-btn" />
            <WhatsAppButton testId="hero-whatsapp-btn" />
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yMark }} className="lg:col-span-5 flex justify-center lg:justify-end">
          <TiltMark />
        </motion.div>
      </div>
    </section>
  );
}
